export type GoogleAttempt = { attempt: string; nonce: string; expiresAt: string };
export type GoogleSession = { token: string; onboarded: boolean };
export type GoogleAuthApi = {
  createGoogleLoginAttempt: () => Promise<GoogleAttempt>;
  completeGoogleLogin: (attempt: string, idToken: string) => Promise<GoogleSession>;
  createGoogleLinkAttempt: (token: string) => Promise<GoogleAttempt>;
  completeGoogleLink: (token: string, attempt: string, idToken: string) => Promise<{ linked: true }>;
};
export class GoogleAuthFlowError extends Error {
  constructor(readonly code: 'busy' | 'unavailable' | 'invalid_response') {
    super(code);
    this.name = 'GoogleAuthFlowError';
  }
}

// One coordinator for both screens: native configuration carries the nonce and
// is global. Never permit concurrent attempts to overwrite each other's nonce.
export function createGoogleAuthFlow(
  api: GoogleAuthApi,
  credential: (nonce: string) => Promise<string | null>,
) {
  let busy = false;
  async function run(
    purpose: 'login' | 'link',
    sessionToken: string | undefined,
    signal?: AbortSignal,
  ): Promise<GoogleSession | { linked: true } | null> {
    if (busy) throw new GoogleAuthFlowError('busy');
    if (signal?.aborted) return null;
    busy = true;
    try {
      if (purpose === 'link' && !sessionToken) throw new GoogleAuthFlowError('unavailable');
      // No caller-supplied attempt/nonce, browser URL, cached provider token, or
      // persisted state is accepted here. Every explicit retry starts anew.
      const attempt = purpose === 'login'
        ? await api.createGoogleLoginAttempt()
        : await api.createGoogleLinkAttempt(sessionToken!);
      if (signal?.aborted) return null;
      if (!attempt || !/^[A-Za-z0-9_-]{43}$/.test(attempt.attempt)
        || !/^[A-Za-z0-9_-]{43}$/.test(attempt.nonce)
        || !Number.isFinite(Date.parse(attempt.expiresAt))) throw new GoogleAuthFlowError('invalid_response');
      const idToken = await credential(attempt.nonce);
      if (signal?.aborted || idToken === null) return null;
      if (typeof idToken !== 'string' || idToken.length > 16_384 || idToken.split('.').length !== 3) {
        throw new GoogleAuthFlowError('invalid_response');
      }
      const result = purpose === 'login'
        ? await api.completeGoogleLogin(attempt.attempt, idToken)
        : await api.completeGoogleLink(sessionToken!, attempt.attempt, idToken);
      if (signal?.aborted) return null;
      if (!result || typeof result !== 'object') throw new GoogleAuthFlowError('invalid_response');
      if (purpose === 'login') {
        if (!('token' in result) || typeof result.token !== 'string' || !result.token
          || typeof result.onboarded !== 'boolean') throw new GoogleAuthFlowError('invalid_response');
      } else if (!('linked' in result) || result.linked !== true) {
        throw new GoogleAuthFlowError('invalid_response');
      }
      return result;
    } finally {
      busy = false;
    }
  }
  return {
    login: (signal?: AbortSignal) => run('login', undefined, signal) as Promise<GoogleSession | null>,
    link: (freshOtpToken: string, signal?: AbortSignal) =>
      run('link', freshOtpToken, signal) as Promise<{ linked: true } | null>,
  };
}
